import axios from 'lib/axios'
import { User, UserData, UserRoleSummary } from 'models'

const basePath = '/api/v4/users'

/* Only the ways of narrowing a member list that the reads below actually send. The full set lives
 * in workflows-v2 and is copied by hand here, so anything listed but unsent would drift unnoticed. */
interface UserFilters {
  id?: { any_: string[] }
  email?: { any_: string[] }
  search?: { like_: string }
  verified?: boolean
}

interface UserFilterBody {
  user?: UserFilters
  page?: number
  limit?: number
}

export interface ListUsersParams {
  page?: number
  limit?: number
  search?: string
  verified?: boolean
}

export interface CanInvite {
  allowed: boolean
  reason?: string
}

export interface InviteUserPayload {
  name: string
  email: string
  role: string
}

/* Every field optional, so each modal sends only the field it changes. */
export interface ProfileUpdate {
  name?: string
  email?: string
  phone_number?: string
}

/* Every member read goes through here. Deliberately not exported: the services barrel re-exports
 * everything, and a component able to reach this would assemble its own request body. */
const filterUsers = async (body: UserFilterBody): Promise<UserData> => {
  const { data } = await axios.post(`${basePath}/filter`, body)
  return data
}

/* The one place the portal talks to workflows-v2 user management. Results come back wrapped
 * inconsistently — under `data`, under `data.list`, or plain — so each function unwraps its own. */
export const UserServiceV4 = {
  listUsers: async (params: ListUsersParams = {}): Promise<UserData> => {
    const { page, limit, search, verified } = params
    const user: UserFilters = {}
    if (search) user.search = { like_: search }
    if (verified !== undefined) user.verified = verified

    const body: UserFilterBody = { page, limit }
    if (Object.keys(user).length > 0) body.user = user

    return filterUsers(body)
  },

  /* Looks members up by id. The page size is the id count, so every member asked for comes back
   * instead of just the first page of them. workflows-v2 caps the list at 500 and no screen here
   * sends near that many, so the ids go in a single request. */
  getUsersByIds: async (userIds: string[]): Promise<User[]> => {
    // No ids would mean a page size of zero, which the server rejects.
    if (userIds.length === 0) return []

    const { users } = await filterUsers({ user: { id: { any_: userIds } }, limit: userIds.length })
    return users
  },

  /* Finds the member whose address is exactly this one. The match is case-sensitive, so an address
   * typed with different capitalisation than the stored one finds nobody. */
  getUserByEmail: async (email: string): Promise<User | undefined> => {
    const { users } = await filterUsers({ user: { email: { any_: [email] } }, limit: 1 })
    return users[0]
  },

  getRoles: async (): Promise<UserRoleSummary[]> => {
    const { data } = await axios.get(`${basePath}/roles`)
    return data.data.list
  },

  getCanInvite: async (): Promise<CanInvite> => {
    const { data } = await axios.get(`${basePath}/can-invite`)
    return data
  },

  getMe: async (): Promise<User> => {
    const { data } = await axios.get(`${basePath}/me`)
    return data.data
  },

  inviteUser: async (payload: InviteUserPayload): Promise<void> => {
    await axios.post(`${basePath}/invite`, payload)
  },

  reinviteUser: async (userId: string): Promise<void> => {
    await axios.post(`${basePath}/${userId}/reinvite`)
  },

  updateUserRole: async (userId: string, role: string): Promise<void> => {
    await axios.post(`${basePath}/${userId}/role`, { role })
  },

  transferOwnership: async (userId: string, role: string): Promise<void> => {
    await axios.post(`${basePath}/${userId}/transfer-ownership`, { role })
  },

  deleteUsers: async (userIds: string[]): Promise<void> => {
    await axios.post(`${basePath}/delete`, { users: userIds })
  },

  deleteUser: async (userId: string): Promise<void> => {
    await axios.delete(`${basePath}/${userId}`)
  },

  deleteMe: async (): Promise<void> => {
    await axios.delete(`${basePath}/me`)
  },

  resetPasswordForUsers: async (userIds: string[]): Promise<void> => {
    await axios.post(`${basePath}/password-reset`, { users: userIds })
  },

  resetPasswordSelf: async (): Promise<void> => {
    await axios.post(`${basePath}/me/password-reset`)
  },

  resetPasswordForUser: async (userId: string): Promise<void> => {
    await axios.post(`${basePath}/${userId}/password-reset`)
  },

  getPasswordResetLink: async (userId: string): Promise<{ link: string }> => {
    const { data } = await axios.get(`${basePath}/${userId}/password-reset-link`)
    return data
  },

  updateProfile: async (target: string, changes: ProfileUpdate): Promise<void> => {
    await axios.patch(`${basePath}/${target}`, changes)
  }
}
